import type { SettlementRow } from '@/api';
import { ChartSkeleton, InfoTooltip } from '@/components';
import { BarChart, CurrencyValue, Flex, Heading } from '@blockanalitica/ui';
import { SimpleChartDataset } from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { useCallback, useMemo } from 'react';

const SERIES_COLORS = {
  income: '#22c55e',
  expenses: '#ef4444',
  net_revenue: '#3b82f6',
  net_profit: '#0ea5e9'
};

const chartConfig = {
  options: {
    interaction: {
      mode: 'index' as const,
      intersect: false,
      axis: 'x' as const
    },
    scales: {
      x: {
        type: 'category' as const,
        stacked: false
      },
      y: {
        stacked: false
      }
    }
  }
};

interface SettlementHistoryChartProps {
  rows?: SettlementRow[];
}

export default function SettlementHistoryChart({
  rows
}: SettlementHistoryChartProps) {
  const datasets = useMemo<SimpleChartDataset[]>(
    () => [
      {
        xField: 'period',
        yField: 'income',
        label: 'Income',
        backgroundColor: SERIES_COLORS.income
      },
      {
        xField: 'period',
        yField: 'expenses',
        label: 'Expenses',
        backgroundColor: SERIES_COLORS.expenses
      },
      {
        xField: 'period',
        yField: 'net_revenue',
        label: 'Net Revenue',
        backgroundColor: SERIES_COLORS.net_revenue
      }
      // {
      //   xField: 'period',
      //   yField: 'net_profit',
      //   label: 'Net Surplus',
      //   backgroundColor: SERIES_COLORS.net_profit
      // }
    ],
    []
  );

  const valueFormatter = useCallback(
    (value: string) => (
      <CurrencyValue
        value={Number(value)}
        options={{ currency: 'USD', notation: 'compact' }}
      />
    ),
    []
  );

  const subValueFormatter = useCallback(
    (value: string | number) => <>{value}</>,
    []
  );

  if (!rows) {
    return <ChartSkeleton />;
  }

  const xScaleValueFormatter = (value: string | number) =>
    rows[Number(value)]?.period ?? '';

  return (
    <Flex flexDirection="column" gap="s">
      <Flex gap="3xs" alignItems="center">
        <Heading size="4">Settlement History</Heading>
        <InfoTooltip text="Income, expenses and net revenue for every executed Monthly Settlement Cycle." />
      </Flex>
      <BarChart
        key="settlement-history"
        data={rows}
        datasets={datasets}
        valueFormatter={valueFormatter}
        subValueFormatter={subValueFormatter}
        xScaleValueFormatter={xScaleValueFormatter}
        config={chartConfig}
      />
    </Flex>
  );
}
